import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button, Card, CardContent } from '@bloomcode/ui';
import { useCookieSession } from '@bloomcode/auth';
import {
  FileText,
  BarChart2,
  CreditCard,
  Shield,
  Clock,
  CheckCircle,
  ArrowRight,
} from '@bloomcode/icons';

export default function HomePage() {
  const { isAuthenticated } = useCookieSession();

  return (
    <>
      <Helmet>
        <title>Bloomcode Ledger Portal - View Your Financial Documents</title>
        <meta
          name="description"
          content="Access your invoices, statements, and payment history securely through the Bloomcode Ledger Portal."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold tracking-tight font-[Manrope]">
              Bloomcode Ledger Portal
            </h1>
            <p className="mt-6 text-xl text-muted-foreground">
              View your invoices, statements, and payment history — all in one secure place.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button size="lg" className="w-full sm:w-auto gap-2">
                    Go to Dashboard
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/auth/login">
                    <Button size="lg" className="w-full sm:w-auto gap-2">
                      Sign In
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/auth/register">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto">
                      Create Account
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-[Manrope]">What You Can Do</h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
              Everything you need to manage your financial relationship with us
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card variant="glass" hover="lift">
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Invoices</h3>
                <p className="text-muted-foreground">
                  View all invoices sent to you, check their status, and download PDFs for your
                  records.
                </p>
              </CardContent>
            </Card>
            <Card variant="glass" hover="lift">
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <BarChart2 className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Statements</h3>
                <p className="text-muted-foreground">
                  Download monthly account statements and track your financial activity over time.
                </p>
              </CardContent>
            </Card>
            <Card variant="glass" hover="lift">
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Payments</h3>
                <p className="text-muted-foreground">
                  View your complete payment history and make online payments for outstanding
                  invoices.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6 font-[Manrope]">
                  Why Use the Ledger Portal?
                </h2>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-success/10 flex items-center justify-center">
                        <Clock className="h-5 w-5 text-success" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold">24/7 Access</h3>
                      <p className="text-muted-foreground">
                        Access your financial documents anytime, anywhere.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Shield className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold">Secure & Private</h3>
                      <p className="text-muted-foreground">
                        Your financial data is protected with enterprise-grade security.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-accent" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold">Always Up to Date</h3>
                      <p className="text-muted-foreground">
                        Real-time sync with your account — no delays, no stale data.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <Card variant="glass">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold mb-4">Ready to get started?</h3>
                  <p className="text-muted-foreground mb-6">
                    {isAuthenticated
                      ? 'Head to your dashboard to view your account.'
                      : 'Sign in to access your invoices, statements, and payment history.'}
                  </p>
                  <div className="space-y-3">
                    {isAuthenticated ? (
                      <Link to="/dashboard" className="block">
                        <Button className="w-full" size="lg">
                          Go to Dashboard
                        </Button>
                      </Link>
                    ) : (
                      <>
                        <Link to="/auth/login" className="block">
                          <Button className="w-full" size="lg">
                            Sign In
                          </Button>
                        </Link>
                        <Link to="/auth/register" className="block">
                          <Button variant="outline" className="w-full" size="lg">
                            Create Account
                          </Button>
                        </Link>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
